import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { SearchBar } from '../components/ListControls';
import { CLIENT_FACILITIES, CLIENT_WALLET_BALANCES } from '../data/mockAdapter';

interface Facility {
  id: string;
  name: string;
  collateralTokens: string[];
  settlementTokens: string[];
  interestRate: string;
  maxLTV: string;
  liquidationThreshold: string;
  utilization: string;
  tvl: string;
  borrowed: string;
  status: 'active' | 'paused';
}

const FACILITIES: Facility[] = CLIENT_FACILITIES.map((facility) => ({
  id: facility.id,
  name: facility.name,
  collateralTokens: facility.collateralTokens,
  settlementTokens: facility.settlementTokens,
  interestRate: facility.interestRate,
  maxLTV: facility.maxLTV,
  liquidationThreshold: facility.liquidationThreshold,
  utilization: facility.utilization,
  tvl: facility.tvl,
  borrowed: facility.borrowed,
  status: facility.status,
}));

const WALLET_BALANCES = CLIENT_WALLET_BALANCES;

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

const SELECT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

export function DepositPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(
    searchParams.get('facilityId'),
  );
  const [selectedToken, setSelectedToken] = useState<string>('');
  const [amount, setAmount] = useState('');
  const [isDepositing, setIsDepositing] = useState(false);
  const [search, setSearch] = useState('');

  const selectedFacility = FACILITIES.find((v) => v.id === selectedFacilityId);
  const activeToken = selectedToken || selectedFacility?.collateralTokens[0] || '';
  const walletBalance = WALLET_BALANCES[activeToken] ?? '0';
  const numericBalance = parseFloat(walletBalance.replace(/,/g, '')) || 0;
  const parsedAmount = parseFloat(amount) || 0;

  const filteredFacilities = useMemo(
    () =>
      FACILITIES.filter((v) => {
        const q = search.toLowerCase();
        return (
          v.name.toLowerCase().includes(q) ||
          v.collateralTokens.some((t) => t.toLowerCase().includes(q))
        );
      }),
    [search],
  );

  function selectFacility(facilityId: string) {
    setSelectedFacilityId(facilityId);
    setSearchParams({ facilityId });
    setAmount('');
    setSelectedToken('');
  }

  function goBack() {
    setSelectedFacilityId(null);
    setSearchParams({});
    setAmount('');
    setSelectedToken('');
  }

  function handleMax() {
    setAmount(numericBalance.toString());
  }

  function handleDeposit() {
    if (parsedAmount <= 0 || parsedAmount > numericBalance) return;
    setIsDepositing(true);
    setTimeout(() => {
      setIsDepositing(false);
      setAmount('');
      toast.success('Deposit submitted successfully');
    }, 2000);
  }

  if (selectedFacility) {
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
          Back to Margin Accounts
        </button>

        <Card>
          <CardHeader className="p-6">
            <CardTitle>Deposit Collateral</CardTitle>
            <CardDescription>Deposit collateral into your margin account ({selectedFacility.name})</CardDescription>
          </CardHeader>
          <CardContent className="p-6 pt-0 space-y-5">
            {selectedFacility.collateralTokens.length > 1 && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Collateral token</label>
                <select
                  value={activeToken}
                  onChange={(e) => {
                    setSelectedToken(e.target.value);
                    setAmount('');
                  }}
                  className={SELECT_CLASS}
                >
                  {selectedFacility.collateralTokens.map((t) => (
                    <option key={t} value={t}>
                      {t}: Balance: {WALLET_BALANCES[t] ?? '0'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Amount to deposit</label>
                <span className="text-sm text-muted-foreground">
                  Balance: {walletBalance} {activeToken}
                </span>
              </div>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="any"
                  className={`${INPUT_CLASS} pr-24`}
                />
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">
                    {activeToken}
                  </span>
                  <button
                    type="button"
                    onClick={handleMax}
                    className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
                  >
                    MAX
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-blue-50 p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Icon name="streamline:interface-alert-information-circle-information-frame-info-more-help-point-circle" className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <span className="font-medium text-blue-900">Credit Facility Terms</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-blue-700">Annual Interest Rate</span>
                  <p className="font-semibold text-green-600">{selectedFacility.interestRate}</p>
                </div>
                <div>
                  <span className="text-blue-700">Liquidation Threshold</span>
                  <p className="font-semibold text-blue-900">
                    {selectedFacility.liquidationThreshold}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={goBack}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                disabled={
                  isDepositing || parsedAmount <= 0 || parsedAmount > numericBalance
                }
                onClick={handleDeposit}
              >
                {isDepositing ? (
                  <>
                    <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="mr-2 h-4 w-4 animate-spin" />
                    Depositing...
                  </>
                ) : (
                  `Deposit ${activeToken}`
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Deposit Collateral</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Select a margin account to deposit collateral into. Each margin account belongs to a credit facility that defines the accepted tokens and terms.
        </p>
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder="Search margin accounts..." className="sm:w-64" />

      {filteredFacilities.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No margin accounts found matching your search.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredFacilities.map((facility) => {
            const isPaused = facility.status === 'paused';

            return (
              <Card key={facility.id}>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">{facility.name}</h3>
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        isPaused
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {facility.status}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Collateral: {facility.collateralTokens.join(', ')}
                  </p>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Interest Rate</span>
                      <p className="font-semibold text-green-600">{facility.interestRate}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Max LTV</span>
                      <p className="font-semibold">{facility.maxLTV}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Settlement</span>
                      <p className="font-semibold">{facility.settlementTokens.join(', ')}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Utilization</span>
                      <p className="font-semibold">{facility.utilization}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-4 w-4" />
                    <span>
                      {facility.collateralTokens
                        .map((t) => `${WALLET_BALANCES[t] ?? '0'} ${t}`)
                        .join(', ')}
                    </span>
                  </div>

                  <Button
                    className="w-full"
                    disabled={isPaused}
                    onClick={() => selectFacility(facility.id)}
                  >
                    {isPaused ? 'Facility Paused' : 'Deposit Collateral →'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
